import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type StripeMode = 'sandbox' | 'production';

interface StripeConfigContextType {
  mode: StripeMode;
  setMode: (mode: StripeMode) => void;
  isSandbox: boolean;
  toggleMode: () => void;
}

const StripeConfigContext = createContext<StripeConfigContextType | undefined>(undefined);

export const StripeConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // In production builds, always use live mode. In development, read from localStorage.
  const [mode, setModeState] = useState<StripeMode>(() => {
    if (import.meta.env.PROD) return 'production';
    try {
      const saved = localStorage.getItem('stripe_mode');
      return (saved === 'production' ? 'production' : 'sandbox') as StripeMode;
    } catch {
      return 'sandbox';
    }
  });

  // Persist mode to localStorage (dev only)
  useEffect(() => {
    if (!import.meta.env.PROD) {
      localStorage.setItem('stripe_mode', mode);
    }
  }, [mode]);

  const setMode = (newMode: StripeMode) => {
    if (import.meta.env.PROD) return;
    setModeState(newMode);
  };

  const toggleMode = () => {
    if (import.meta.env.PROD) return;
    setModeState(prev => prev === 'sandbox' ? 'production' : 'sandbox');
  };

  const isSandbox = mode === 'sandbox';

  return (
    <StripeConfigContext.Provider value={{ mode, setMode, isSandbox, toggleMode }}>
      {children}
    </StripeConfigContext.Provider>
  );
};

export const useStripeConfig = () => {
  const context = useContext(StripeConfigContext);
  if (context === undefined) {
    throw new Error('useStripeConfig must be used within a StripeConfigProvider');
  }
  return context;
};
